/**
 * CacheStatsCard — Prompt-cache savings dashboard.
 *
 * Shows accumulated cache_read / cache_write tokens stored in usage_events
 * (populated from native Anthropic / OpenAI / Gemini / DeepSeek chat
 * clients' last_cache_* attrs) plus an estimated cost saving.
 *
 * Rendered inside UnitEconomicsPanel so it inherits the same period controls.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Zap, TrendingDown, Database, BarChart3 } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const fmt  = (n) => Number(n || 0).toLocaleString();
const usd4 = (n) => `$${Number(n || 0).toFixed(4)}`;
const usd2 = (n) => `$${Number(n || 0).toFixed(2)}`;

const PROVIDER_COLORS = {
  anthropic: 'bg-amber-100 text-amber-800 border-amber-200',
  openai:    'bg-emerald-100 text-emerald-800 border-emerald-200',
  gemini:    'bg-blue-100 text-blue-800 border-blue-200',
  google:    'bg-blue-100 text-blue-800 border-blue-200',
  deepseek:  'bg-purple-100 text-purple-800 border-purple-200',
  unknown:   'bg-zinc-100 text-zinc-700 border-zinc-200',
};

function ProviderBadge({ provider }) {
  const cls = PROVIDER_COLORS[provider] || PROVIDER_COLORS.unknown;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-[1px] text-[10px] font-semibold capitalize ${cls}`}
    >
      {provider}
    </span>
  );
}

function StatBox({ label, value, sub, icon: Icon, testId }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        {Icon && <Icon className="h-3 w-3" />}{label}
      </div>
      <div className="mt-1 font-mono text-[18px] font-semibold">{value}</div>
      {sub && <div className="mt-0.5 text-[10.5px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function CacheStatsCard({ from, to }) {
  const [data, setData]   = useState(null);
  const [busy, setBusy]   = useState(false);
  const [err,  setErr]    = useState(null);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const q = new URLSearchParams({
        ...(from && { from }),
        ...(to   && { to   }),
      }).toString();
      const res = await axios.get(
        `${API}/admin/cache-stats${q ? `?${q}` : ''}`,
        { withCredentials: true },
      );
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load cache stats.');
    } finally {
      setBusy(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const totals   = data?.totals   || {};
  const byProv   = data?.by_provider || [];
  const trend    = data?.trend    || [];
  const maxRead  = Math.max(...trend.map((d) => d.cache_read_tokens || 0), 1);

  return (
    <div
      className="mt-4 rounded-md border border-border bg-card"
      data-testid="cache-stats-card"
    >
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="flex items-center gap-2">
          <Zap className="h-3.5 w-3.5 text-amber-500" />
          <span className="text-[11.5px] font-semibold">Prompt-Cache Savings</span>
        </div>
        <span className="text-[10.5px] text-muted-foreground">
          cache_read × provider discount
        </span>
      </div>

      {busy && (
        <div className="flex items-center justify-center p-6 text-[12px] text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading cache stats…
        </div>
      )}
      {err && (
        <div className="p-4 text-center text-[12px] text-red-500" data-testid="cache-stats-error">
          {err}
        </div>
      )}

      {!busy && !err && data && (
        <div className="p-3 space-y-3">
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <StatBox
              label="Est. saved"
              value={usd2(totals.est_saved_usd)}
              sub="vs. uncached token price"
              icon={TrendingDown}
              testId="cache-kpi-saved"
            />
            <StatBox
              label="Cache reads"
              value={fmt(totals.cache_read_tokens)}
              sub="tokens served from cache"
              icon={Zap}
              testId="cache-kpi-reads"
            />
            <StatBox
              label="Cache writes"
              value={fmt(totals.cache_write_tokens)}
              sub="new cache entries written"
              icon={Database}
              testId="cache-kpi-writes"
            />
            <StatBox
              label="Calls with hit"
              value={fmt(totals.calls_with_cache_hit)}
              sub="generation calls that hit a cache"
              icon={BarChart3}
              testId="cache-kpi-hits"
            />
          </div>

          {/* Provider breakdown */}
          {byProv.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-[12px]" data-testid="cache-by-provider">
                <thead className="bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Provider</th>
                    <th className="px-3 py-1.5 text-right">Cache reads</th>
                    <th className="px-3 py-1.5 text-right">Cache writes</th>
                    <th className="px-3 py-1.5 text-right">Hits</th>
                    <th className="px-3 py-1.5 text-right">Est. saved</th>
                  </tr>
                </thead>
                <tbody>
                  {byProv.map((p) => (
                    <tr
                      key={p.provider}
                      className="border-t border-border/60"
                      data-testid={`cache-row-${p.provider}`}
                    >
                      <td className="px-3 py-1.5"><ProviderBadge provider={p.provider} /></td>
                      <td className="px-3 py-1.5 text-right font-mono">{fmt(p.cache_read_tokens)}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{fmt(p.cache_write_tokens)}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{fmt(p.calls_with_hit)}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-emerald-700 font-semibold">
                        {usd4(p.est_saved_usd)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Daily trend sparkbar */}
          {trend.length > 1 && (
            <div data-testid="cache-trend">
              <div className="mb-1.5 text-[10.5px] text-muted-foreground uppercase tracking-wider">
                Daily cache reads
              </div>
              <div className="flex items-end gap-[2px] h-10">
                {trend.map((d) => {
                  const heightPct = Math.max(4, Math.round((d.cache_read_tokens / maxRead) * 100));
                  return (
                    <div
                      key={d.date}
                      title={`${d.date}: ${fmt(d.cache_read_tokens)} reads · ${usd4(d.est_saved_usd)} saved`}
                      className="flex-1 bg-amber-400/70 rounded-sm min-w-[3px] cursor-default hover:bg-amber-500 transition-colors"
                      style={{ height: `${heightPct}%` }}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {byProv.length === 0 && (
            <p className="py-2 text-center text-[12px] text-muted-foreground">
              No cache activity recorded in this period. Cache stats are captured for
              native (BYOK) Anthropic, OpenAI, Gemini, and DeepSeek routes.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
